/**
 * Number Formatting Utilities
 * Centralized number formatting functions used across the application
 */

/**
 * Safely parse a value to a float number with a fallback
 * @param value - The value to parse (string or number)
 * @param fallback - The fallback value if parsing fails (default: 0)
 * @returns Parsed float or fallback value
 */
export function safeParseFloat(value: string | number | null | undefined, fallback: number = 0): number {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'number') return isNaN(value) ? fallback : value;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? fallback : parsed;
}

/**
 * Safely parse a value to an integer with a fallback
 * @param value - The value to parse (string or number)
 * @param fallback - The fallback value if parsing fails (default: 0)
 * @returns Parsed integer or fallback value
 */
function safeParseInt(value: string | number | null | undefined, fallback: number = 0): number {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'number') return isNaN(value) ? fallback : value;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? fallback : parsed;
}

/**
 * Format a number as a percentage string
 * @param value - The value to format
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string (e.g., "45.2%")
 */
function formatPercent(value: string | number | null | undefined, decimals: number = 1): string {
  const numeric = safeParseFloat(value);
  return `${numeric.toFixed(decimals)}%`;
}

/**
 * Format a success/completion rate as a percentage
 * @param rate - The rate value (can be string or number)
 * @returns Formatted rate string (e.g., "85.0%")
 */
function formatSuccessRate(rate: string | number | null | undefined): string {
  return formatPercent(rate, 1);
}

/**
 * Format seconds as a duration string (e.g., "5m 30s")
 * @param seconds - The number of seconds (string or number)
 * @returns Formatted duration string (e.g., "5m 30s")
 */
export function formatDuration(seconds: string | number | null | undefined): string {
  const numSeconds = safeParseFloat(seconds);
  const minutes = Math.floor(numSeconds / 60);
  const remainingSeconds = Math.floor(numSeconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Format LinkedIn connections count with thousand separators
 * @param connections - Number of connections (string or number)
 * @returns Formatted connections string (e.g., "1,234")
 */
function formatConnections(connections?: string | number): string {
  if (!connections) return 'N/A';
  const num = typeof connections === 'string' ? parseInt(connections, 10) : connections;
  if (Number.isNaN(num)) return 'N/A';
  return num.toLocaleString();
}

/**
 * Format a number with thousand separators
 * @param value - The number to format
 * @returns Formatted number string (e.g., "1,234,567")
 */
function formatNumber(value: string | number | null | undefined): string {
  const num = safeParseFloat(value);
  return num.toLocaleString();
}

/**
 * Format a large number as abbreviated string (e.g., 1.5M, 2.3B)
 * @param value - The number to format (string or number)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Abbreviated string (e.g., "1.5M", "2.3B", "500K")
 */
function formatAbbreviatedNumber(value: string | number | null | undefined, decimals: number = 1): string {
  const num = safeParseFloat(value);
  if (num === 0) return '0';

  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  if (absNum >= 1e12) {
    return `${sign}${(absNum / 1e12).toFixed(decimals)}T`;
  }
  if (absNum >= 1e9) {
    return `${sign}${(absNum / 1e9).toFixed(decimals)}B`;
  }
  if (absNum >= 1e6) {
    return `${sign}${(absNum / 1e6).toFixed(decimals)}M`;
  }
  if (absNum >= 1e3) {
    return `${sign}${(absNum / 1e3).toFixed(decimals)}K`;
  }

  return num.toLocaleString();
}

/**
 * Format a byte count as a human-readable file size string
 * @param bytes - The number of bytes
 * @returns Formatted size string (e.g., "1.2 MB", "340 KB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * Format revenue value with appropriate abbreviation and currency symbol
 * @param value - Revenue value (string or number, can include currency symbols)
 * @param currency - Currency symbol to use (default: '$')
 * @returns Formatted revenue string (e.g., "$1.5M", "$2.3B")
 */
export function formatRevenue(value: string | number | null | undefined, currency: string = '$'): string {
  if (value === null || value === undefined) return '-';

  // If it's already a string with letters (like "1.5M" or "$10M"), return as-is
  if (typeof value === 'string') {
    const cleanedValue = value.replace(/[$,\s]/g, '');
    if (/[A-Za-z]/.test(cleanedValue)) {
      // Already formatted, just ensure currency symbol
      const hasSymbol = value.trim().startsWith(currency);
      return hasSymbol ? value : `${currency}${cleanedValue}`;
    }
  }

  const formatted = formatAbbreviatedNumber(value);
  if (formatted === '0' || formatted === '-') return '-';

  return `${currency}${formatted}`;
}
